import React, { useState } from 'react';

/**
 * RegistrationForm — CoThink Compass
 * Captures user information before starting the assessment.
 * VISUAL RESTYLE ONLY: field names (email, full_name, job_title, honeypot),
 * validation rules, and the onStart payload are unchanged.
 */
const RegistrationForm = ({ onStart }) => {
  const [formData, setFormData] = useState({
    email: '',
    full_name: '',
    job_title: '',
    honeypot: '' // Anti-spam honeypot field
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Form validation
  const validate = () => {
    const newErrors = {};

    // Check honeypot (should be empty)
    if (formData.honeypot) {
      newErrors.honeypot = 'Bot detected';
      return newErrors;
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!formData.email) {
      newErrors.email = 'Email is required';
    } else if (!emailRegex.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    // Full name validation
    if (!formData.full_name || formData.full_name.trim().length < 2) {
      newErrors.full_name = 'Please enter your full name (at least 2 characters)';
    }

    // Job title validation
    if (!formData.job_title || formData.job_title.trim().length < 2) {
      newErrors.job_title = 'Please enter your job title (at least 2 characters)';
    }

    return newErrors;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setIsSubmitting(true);
    try {
      const userData = {
        email: formData.email.trim(),
        full_name: formData.full_name.trim(),
        job_title: formData.job_title.trim()
      };
      await onStart(userData);
    } catch (error) {
      console.error('Error in form submission:', error);
      setErrors({ submit: error.message || 'Failed to start assessment. Please try again.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const inputClasses = (hasError) =>
    `w-full px-4 py-3 rounded-lg border bg-paper-50 dark:bg-navy-900 text-navy-900 dark:text-paper-100 ` +
    `placeholder-navy-300 dark:placeholder-navy-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 ` +
    `focus:border-transparent transition-colors ${hasError ? 'border-red-400' : 'border-paper-500 dark:border-navy-600'}`;

  return (
    <div className="min-h-screen pt-10 pb-16 bg-navy-50 dark:bg-navy-900">
      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <p className="text-interface font-semibold text-emerald-600 dark:text-emerald-300 uppercase tracking-[0.2em] mb-4">
            A Diagnose Instrument
          </p>
          <h1 className="font-display text-display-md text-navy-900 dark:text-paper-100 mb-4">
            The CoThink Compass
          </h1>
          <p className="text-xl text-navy-600 dark:text-paper-300">
            Discover your cognitive engagement patterns with AI
          </p>
          <div className="mt-6 flex items-center justify-center gap-6 text-interface text-navy-500 dark:text-paper-400 flex-wrap">
            <span className="flex items-center">
              <svg className="w-5 h-5 mr-2 text-emerald-600 dark:text-emerald-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              10 minutes
            </span>
            <span className="flex items-center">
              <svg className="w-5 h-5 mr-2 text-emerald-600 dark:text-emerald-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              Immediate results
            </span>
            <span className="flex items-center">
              <svg className="w-5 h-5 mr-2 text-emerald-600 dark:text-emerald-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              PDF report
            </span>
          </div>
        </div>

        {/* Registration Form */}
        <div className="bg-paper-50 dark:bg-navy-800 rounded-2xl shadow-sm p-8 border border-paper-400 dark:border-navy-700">
          <h2 className="font-display text-2xl font-bold text-navy-900 dark:text-paper-100 mb-6">Get Started</h2>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Email Field */}
            <div>
              <label htmlFor="email" className="block text-interface font-semibold text-navy-800 dark:text-paper-200 mb-2">
                Email Address <span className="text-emerald-600 dark:text-emerald-300">*</span>
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                className={inputClasses(errors.email)}
                placeholder="you@example.com"
                disabled={isSubmitting}
              />
              {errors.email && (
                <p className="mt-2 text-interface text-red-600 dark:text-red-400">{errors.email}</p>
              )}
            </div>

            {/* Full Name Field */}
            <div>
              <label htmlFor="full_name" className="block text-interface font-semibold text-navy-800 dark:text-paper-200 mb-2">
                Full Name <span className="text-emerald-600 dark:text-emerald-300">*</span>
              </label>
              <input
                type="text"
                id="full_name"
                name="full_name"
                value={formData.full_name}
                onChange={handleChange}
                className={inputClasses(errors.full_name)}
                placeholder="e.g. Alex Rivera"
                disabled={isSubmitting}
              />
              {errors.full_name && (
                <p className="mt-2 text-interface text-red-600 dark:text-red-400">{errors.full_name}</p>
              )}
            </div>

            {/* Job Title Field */}
            <div>
              <label htmlFor="job_title" className="block text-interface font-semibold text-navy-800 dark:text-paper-200 mb-2">
                Job Title <span className="text-emerald-600 dark:text-emerald-300">*</span>
              </label>
              <input
                type="text"
                id="job_title"
                name="job_title"
                value={formData.job_title}
                onChange={handleChange}
                className={inputClasses(errors.job_title)}
                placeholder="e.g. VP of Engineering"
                disabled={isSubmitting}
              />
              {errors.job_title && (
                <p className="mt-2 text-interface text-red-600 dark:text-red-400">{errors.job_title}</p>
              )}
            </div>

            {/* Honeypot field - hidden from users */}
            <input
              type="text"
              name="honeypot"
              value={formData.honeypot}
              onChange={handleChange}
              className="hidden"
              tabIndex="-1"
              autoComplete="off"
            />

            {/* Submit Error */}
            {errors.submit && (
              <div className="p-4 bg-red-50 dark:bg-red-950 border border-red-200 dark:border-red-800 rounded-lg">
                <p className="text-interface text-red-600 dark:text-red-300">{errors.submit}</p>
              </div>
            )}

            {/* Submit Button */}
            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full py-4 px-6 rounded-lg font-semibold text-lg transition-colors bg-emerald-600 hover:bg-emerald-700 text-white disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {isSubmitting ? (
                <span className="flex items-center justify-center">
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Starting Assessment...
                </span>
              ) : (
                'Begin Assessment'
              )}
            </button>
          </form>

          {/* Privacy Note */}
          <p className="mt-6 text-interface text-navy-500 dark:text-paper-400 text-center">
            Your information is secure and will only be used for assessment purposes.
            <br />
            We respect your privacy and will never share your data.
          </p>
        </div>
      </div>
    </div>
  );
};

export default RegistrationForm;
